import { ApplicationConfig, importProvidersFrom, ErrorHandler, isDevMode, APP_INITIALIZER } from '@angular/core';
import { provideRouter } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { routes } from './app.routes';
import { authHttpInterceptorFn, provideAuth0 } from '@auth0/auth0-angular';
import { httpRetryInterceptor } from 'src/app/core/interceptors/http-retry.interceptor';
import { httpErrorInterceptor } from 'src/app/core/interceptors/http-error.interceptor';
import { clientTelemetryInterceptor } from 'src/app/core/interceptors/client-telemetry.interceptor';
import { requestInstrumentationInterceptor } from 'src/app/core/interceptors/request-instrumentation.interceptor';
import { httpTimeoutInterceptor } from 'src/app/core/interceptors/http-timeout.interceptor';
import { mockGeneticsInterceptor } from 'src/app/core/interceptors/mock-genetics.interceptor';
import { provideAnimations } from '@angular/platform-browser/animations';
import { HttpClient, provideHttpClient, withInterceptors } from '@angular/common/http';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { environment } from 'src/environments/environment';
import { GlobalErrorHandler } from './core/interceptors/global-error.handler';
import { MAT_DATE_LOCALE, MAT_DATE_FORMATS, MatNativeDateModule } from '@angular/material/core';
import { MY_DATE_FORMATS } from './shared/utils/date.format';
import { provideServiceWorker } from '@angular/service-worker';
import { Capacitor } from '@capacitor/core';
import { ClientTelemetryContextService } from './core/services/client-telemetry-context.service';
import { applyDocumentLanguage } from './core/i18n/document-language';

// AoT requires an exported function for factories
export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

/**
 * App-wide baseline language bootstrap.
 *
 * Without this, `setDefaultLang`/`use` are only called per-component (main-layout,
 * callback, onboarding, …), and several of those run only AFTER a successful
 * profile load. If startup fails early (e.g. `getProfile` times out), no language
 * is ever activated, so `TranslateService.instant(key)` returns the raw KEY and
 * error toasts leak keys like `CONTROL_MESSAGES.ERROR.GET_PROFILE`.
 *
 * Activating `en` (and awaiting its bundle) before the app renders guarantees a
 * synchronous fallback exists for `instant(...)`. Per-component `use()` calls
 * still switch to the user's language later.
 *
 * Resilience notes:
 * - The bundle load is best-effort: if `/assets/i18n/en.json` fails to load
 *   (offline first run, cache miss, CDN hiccup) we SWALLOW the error and still
 *   resolve, so Angular never fails to bootstrap (a rejected APP_INITIALIZER
 *   promise = white screen — the opposite of the resilience this guards).
 * - We synchronously seed the generic error key so `CONTROL_MESSAGES.ERROR.GENERIC`
 *   ALWAYS resolves to real text, even when no bundle loaded. This lets the error
 *   toast fall back to a translated message (never a raw key, never a hardcoded
 *   UI string in a component/service). Seeded with merge=true so it never
 *   clobbers a successfully-loaded bundle.
 */
export const GENERIC_ERROR_SEED = {
  CONTROL_MESSAGES: { ERROR: { GENERIC: 'There was an Application error' } },
};

export function initializeDefaultLanguage(translate: TranslateService): () => Promise<unknown> {
  return async () => {
    translate.setDefaultLang('en');

    // Keep <html lang> tracking the active language for the life of the app.
    // Native date and time inputs format themselves from it, so without this
    // every caregiver gets US formatting: a Spanish start date of 08/02/2026
    // means 2 August but reads as 8 February. Language is switched from ~15
    // places at runtime, so this subscribes rather than firing only at boot.
    applyDocumentLanguage(translate.currentLang || translate.getDefaultLang());
    translate.onLangChange.subscribe(event => applyDocumentLanguage(event.lang));

    try {
      await firstValueFrom(translate.use('en'));
    } catch {
      // Swallow: continue booting with whatever translations are available.
    }
    // Guarantee the generic error key resolves even if the bundle failed to load.
    translate.setTranslation('en', GENERIC_ERROR_SEED, true);
  };
}

/**
 * Resolve the native app versionName (spec 034) before the app renders, so the
 * client telemetry interceptor can attach `X-Rettx-App-Version` synchronously to
 * the very first API requests. No-op on the web (uses `environment.appVersion`).
 * Best-effort: never blocks or fails bootstrap.
 */
export function initializeAppVersion(
  telemetryContext: ClientTelemetryContextService,
): () => Promise<unknown> {
  return async () => {
    try {
      await telemetryContext.initAppVersion();
    } catch {
      // Swallow: fall back to the default/web version.
    }
  };
}

// On the native Capacitor shell, Auth0 must redirect to a custom-scheme URL so
// the system browser can hand the result back to the installed app. On the web
// this resolves to the standard hosted `/callback`, leaving web behavior intact.
const auth0RedirectUri = Capacitor.isNativePlatform()
  ? environment.auth.nativeRedirectUri
  : environment.auth.redirectUri;

/**
 * OAuth scopes requested at login.
 *
 * `offline_access` is REQUIRED to receive a refresh token from Auth0. Without a
 * refresh token, `getAccessTokenSilently()` on native falls back to a hidden
 * iframe `checkSession` that cannot complete inside a Capacitor WebView and
 * hangs until spa-js's internal ~60s authorize timeout — the exact cold-start
 * "colgada"/unicorn hang this config fixes. The Auth0 tenant only issues a
 * refresh token when BOTH the app requests `offline_access` AND the target API
 * (audience `https://api.rettx.eu`) has "Allow Offline Access" enabled.
 */
export const AUTH0_SCOPE = 'openid profile email offline_access';

/**
 * Build the `provideAuth0(...)` configuration.
 *
 * Exported (not inlined) so it can be unit-tested: the refresh-token flags, the
 * `offline_access` scope and the `httpInterceptor.allowedList` (which must
 * bearer ONLY our own API and mint tokens for the new `https://api.rettx.eu`
 * audience) are all correctness-critical for avoiding the native hang and 401s.
 */
export function buildAuth0Config() {
  return {
    // Persist the session (and, critically, the ROTATING refresh token) in
    // localStorage so it survives WebView restarts on native. Required for the
    // refresh-token flow below to work across cold starts.
    cacheLocation: environment.auth.cacheLocation,
    domain: environment.auth.domain,
    clientId: environment.auth.clientId,
    // Use a refresh token instead of hidden-iframe silent auth. On native the
    // iframe path cannot complete and hangs ~60s; a refresh token lets
    // `getAccessTokenSilently()` mint access tokens directly.
    useRefreshTokens: true,
    // CRITICAL: never fall back to the iframe silent-auth when the refresh token
    // is missing/expired. Instead spa-js throws a clear `login_required`-style
    // error immediately, which our resolver/interceptor can handle — no 60s hang.
    useRefreshTokensFallback: false,
    // Raise the auth0-spa-js internal HTTP timeout for token requests from its
    // 10s default to 30s. On slow/flaky mobile networks the startup
    // `getAccessTokenSilently()` call (made by the outermost authHttpInterceptorFn
    // when attaching the bearer token for `${apiUrl}/*`) could exceed the 10s
    // fetch timeout and throw Error("Timeout"). That error originates OUTSIDE the
    // retry/error HTTP interceptors, so it bypassed them and surfaced as an
    // unhandled RxJS error → GlobalErrorHandler → the generic /global-error page.
    httpTimeoutInSeconds: 30,
    authorizationParams: {
      response_type: 'code',
      redirect_uri: auth0RedirectUri,
      audience: environment.auth.audience,
      // Must include `offline_access` to receive a refresh token (see AUTH0_SCOPE).
      scope: AUTH0_SCOPE,
    },
    skipRedirectCallback: true,
    httpInterceptor: {
      // Bearer the token ONLY on requests to our own API base URL, and mint it
      // for the API audience. We deliberately do NOT add an entry for the
      // audience URL itself: the audience is an Auth0 API *identifier*, not an
      // endpoint we call, and the old audience pointed at the Auth0 domain —
      // attaching a bearer there is both useless and a token-leak risk.
      allowedList: [
        {
          uri: `${environment.apiUrl}/*`,
          tokenOptions: {
            authorizationParams: {
              // Derived from env so it stays in lockstep with the audience switch.
              audience: environment.auth.audience,
            },
          },
        },
      ],
    },
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideHttpClient(
      withInterceptors([
        // 0) DEV-ONLY genetics upload mock (opt-in via ?geneticsDemo=...). No-op
        //    in production builds and when the demo flag is not set.
        ...(environment.production ? [] : [mockGeneticsInterceptor]),
        // 1) Request instrumentation - outermost production interceptor so one
        //    logical wait covers token acquisition, retries and timeout handling.
        requestInstrumentationInterceptor,
        // 2) Auth0 Interceptor - adds bearer token to API requests
        authHttpInterceptorFn,
        // 3) Client telemetry headers (spec 034) - attaches
        //    X-Rettx-Correlation-Id + X-Rettx-App-Version to EVERY API request,
        //    independent of the Auth0 allowedList / bearer token, so even the
        //    tokenless cold-start requests we are hunting carry the correlation id.
        clientTelemetryInterceptor,
        // 4) Retry interceptor - retries transient 5xx errors with exponential backoff
        httpRetryInterceptor,
        // 5) Error interceptor - handles errors after retries exhausted
        httpErrorInterceptor,
        // 6) GET timeout - innermost so the existing error interceptor turns a
        //    measured timeout into the normal recoverable error-card path.
        //    The 30s ceiling comes from production endpoint percentiles.
        httpTimeoutInterceptor
      ]),
    ),
    { provide: MAT_DATE_LOCALE, useValue: 'en-GB' },
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS },
    importProvidersFrom(MatNativeDateModule),
    provideRouter(routes),
    importProvidersFrom(
      TranslateModule.forRoot({
        defaultLanguage: 'en',
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient],
        },
      }),
      MatSnackBarModule
    ),
    {
      // Guarantee a baseline language bundle ('en') is loaded before the app
      // renders, so TranslateService.instant() never returns raw keys at startup.
      provide: APP_INITIALIZER,
      useFactory: initializeDefaultLanguage,
      deps: [TranslateService],
      multi: true,
    },
    {
      // Resolve the native app version (spec 034) before render so telemetry
      // headers carry the real versionName on the first API requests.
      provide: APP_INITIALIZER,
      useFactory: initializeAppVersion,
      deps: [ClientTelemetryContextService],
      multi: true,
    },
    provideAuth0(buildAuth0Config()),
    provideAnimations(),
    // Global error handler
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler,
    },
    provideServiceWorker('ngsw-worker.js', {
      // Disable the service worker inside the native Capacitor shell. The app's
      // assets are already bundled locally in the APK, so the SW adds no value
      // there and actively harms us: it caches the app shell/JS at the WebView
      // origin, and WebView storage survives APK reinstalls, so old code keeps
      // running after an update. Keep it enabled for the deployed web PWA.
      enabled: !isDevMode() && !Capacitor.isNativePlatform(),
      registrationStrategy: 'registerWhenStable:30000'
    }),
  ]
};